import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { createUserSubscription } from "../features/admin/slice/subcriptionSlice";
import { toast } from "react-toastify";
import useAuthRole from "../hooks/useAuthRole";
import Modal from "../components/common/Modal";
import Index from "./auth/Index";

const PlanCard = ({ plan, index, isLoggedIn, userSubscriptions }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { user, isAuthenticated, isAdmin } = useAuthRole();


  const isSubscribedToPlan = userSubscriptions?.some(
    (sub) => sub.isActive && sub.plan?.id === plan.id
  );

  const isPro = plan.type === "PRO_ACCESS";
  const isFree = plan.type === "FREE_STARTER";
  const isMentorship = plan.type === "ONE_ON_ONE_MENTORSHIP";
  const isBootcamp = plan.type === "SIX_WEEKS_BOOTCAMP";

  const openAuthModal = () => {
    setIsModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsModalOpen(false);
  };

  const handleLoginSuccess = () => {
    closeAuthModal();

    if (user) {
      handleSubscriptionAfterLogin();
    }
  };

  const handleSubscriptionAfterLogin = async () => {
    if (plan.type === "ONE_ON_ONE_MENTORSHIP") {
      navigate("/schedule-mentorship");
      return;
    }

    try {
      setIsLoading(true);
      const result = await dispatch(
        createUserSubscription({
          userId: user.id,
          planId: plan.id,
        })
      ).unwrap();

      if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
      } else {
        toast.success(`Successfully subscribed to ${plan.name}!`);
        navigate(user.role === "admin" ? "/admin/courses" : "/student/courses");
      }
    } catch (error) {
      toast.error(`Subscription failed: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated || !user) {
      openAuthModal();
      return;
    }

    if (plan.type === "ONE_ON_ONE_MENTORSHIP") {
      navigate("/schedule-mentorship");
      return;
    }

    // If user is already subscribed to this plan, redirect to dashboard
    if (isSubscribedToPlan) {
      navigate(isAdmin ? "/admin/courses" : "/student/courses");
      return;
    }

    try {
      setIsLoading(true);
      const result = await dispatch(
        createUserSubscription({
          userId: user.id,
          planId: plan.id,
        })
      ).unwrap();

      if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
      } else {
        toast.success(`Successfully subscribed to ${plan.name}!`);
        navigate(user.role === "admin" ? "/admin/courses" : "/student/courses");
      }
    } catch (error) {
      toast.error(`Subscription failed: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  let bgColor = "#1B3673";
  let buttonColor = "#1B3673";

  if (isPro) {
    bgColor = "#F5A404";
    buttonColor = "#F5A404";
  } else if (isBootcamp) {
    bgColor = "#FFB625";
    buttonColor = "#FFB625";
  }

  const formattedPrice = Number(plan.price).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const shouldShowAccessButton = () => {
    if (!isLoggedIn) return true;
    if (isAdmin) return true;
    return user.role === "user";
  };


  let buttonText = "Get Free Access";

  if (isSubscribedToPlan) {
    buttonText = "View Dashboard";
  } else if (isPro) {
    buttonText = "Upgrade to Pro";
  } else if (isMentorship) {
    buttonText = "Schedule a Call";
  } else if (isBootcamp) {
    buttonText = "Enroll in Bootcamp";
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="relative w-full pt-2 pl-2"
      >
        <div
          className={`absolute inset-x-0 top-0 ${
            isPro ? "lg:h-full" : "lg:h-[40%]"
          } rounded-xl shadow-lg transform translate-x-4`}
          style={{ backgroundColor: bgColor }}
        ></div>

        <div className="relative bg-white rounded-xl p-6 shadow-md transform -translate-x-2 -translate-y-2">
          <div className="space-y-2 mb-6">
            <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
            <div className="flex items-baseline">
              {isMentorship || isBootcamp ? (
                <span className="text-xl font-bold text-gray-900">
                  {isMentorship ? "Contact Us" : "Contact for Price"}
                </span>
              ) : (
                <span className="text-4xl font-bold text-gray-900">
                  {formattedPrice}
                </span>
              )}

              <span className="ml-2 text-gray-600">
                {isFree ? "/forever" : isMentorship ? "/Custom" : ""}
              </span>
            </div>

            {isSubscribedToPlan && (
              <div className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                Active Subscription
              </div>
            )}
          </div>

          <div className="space-y-4 mb-8">
            <ul className={isPro ? "space-y-6" : "space-y-3"}>
              {plan.permissions &&
                plan.permissions.map((permission, i) => (
                  <li
                    key={i}
                    className={isPro ? "space-y-2" : "flex items-start"}
                  >
                    {isPro ? (
                      <>
                        <div className="flex items-start">
                          <Check className="h-5 w-5 text-[#1B3673] mr-3 mt-0.5 flex-shrink-0" />
                          <p className="text-sm font-bold text-[#1B3673]">
                            {permission.title}
                          </p>
                        </div>
                        {permission.description && (
                          <p className="text-sm text-gray-600 ml-8">
                            {permission.description}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <Check className="h-5 w-5 text-[#1B3673] mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#1B3673]">
                          {permission.title}
                        </span>
                      </>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        </div>

        {shouldShowAccessButton() && (
          <div className="relative mt-1 transform translate-x-4">
            <button
              onClick={handleSubscribe}
              disabled={isLoading}
              className="block w-full text-white text-left px-6 py-3 rounded-lg font-semibold transition-colors duration-200 uppercase"
              style={{ backgroundColor: buttonColor }}
            >
              {isLoading ? "Processing..." : buttonText}
            </button>
          </div>
        )}
      </motion.div>

  
      <Modal isOpen={isModalOpen} onClose={closeAuthModal}>
        <Index onLoginSuccess={handleLoginSuccess} />
      </Modal>
    </>
  );
};

export default PlanCard;
